import React, { useState } from 'react';
import {
  PageSection,
  AnimationConfig,
  AnimationPreset,
  AnimationEasing,
  DEFAULT_ANIMATION_CONFIG,
  ANIMATION_PRESET_OPTIONS,
  DURATION_PRESETS,
  DELAY_PRESETS,
  EASING_OPTIONS,
} from '../../../types';
import { validateAnimationConfig } from '../../../utils/animationEngine';
import {
  Sparkles,
  Play,
  RotateCcw,
  Copy,
  Check,
  AlertTriangle,
  Monitor,
  Tablet,
  Smartphone,
  MousePointer,
  ScrollText,
  Sliders,
  Flame,
} from 'lucide-react';

interface AnimationTabProps {
  section: PageSection;
  allPageSections?: PageSection[];
  onUpdateAnimation: (newAnimation: AnimationConfig) => void;
  onPreviewAnimation?: () => void;
}

// In-memory clipboard for copying animation config between sections
let animationClipboard: AnimationConfig | null = null;

export const AnimationTab: React.FC<AnimationTabProps> = ({
  section,
  allPageSections = [],
  onUpdateAnimation,
  onPreviewAnimation,
}) => {
  const currentConfig: AnimationConfig = validateAnimationConfig(
    section.animation || section.style?.animation
  );

  const [activeDeviceTab, setActiveDeviceTab] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [copiedSuccess, setCopiedSuccess] = useState(false);
  const [pasteSuccess, setPasteSuccess] = useState(false);

  // Calculate page-level animation count for performance audit
  const pageAnimationCount = allPageSections.filter(
    (s) => s.animation?.enabled || s.style?.animation?.enabled
  ).length;

  const handleToggleEnabled = (enabled: boolean) => {
    onUpdateAnimation({
      ...currentConfig,
      enabled,
    });
  };

  const handleUpdateEntrance = (field: string, value: any) => {
    const updated = {
      ...currentConfig,
      entrance: {
        ...(currentConfig.entrance || DEFAULT_ANIMATION_CONFIG.entrance!),
        [field]: value,
      },
    };
    onUpdateAnimation(validateAnimationConfig(updated));
  };

  const handleUpdateScroll = (field: string, value: any) => {
    const updated = {
      ...currentConfig,
      scroll: {
        ...(currentConfig.scroll || DEFAULT_ANIMATION_CONFIG.scroll!),
        [field]: value,
      },
    };
    onUpdateAnimation(validateAnimationConfig(updated));
  };

  const handleUpdateHover = (field: string, value: any) => {
    const updated = {
      ...currentConfig,
      hover: {
        ...(currentConfig.hover || DEFAULT_ANIMATION_CONFIG.hover!),
        [field]: value,
      },
    };
    onUpdateAnimation(validateAnimationConfig(updated));
  };

  const handleResetAnimation = () => {
    onUpdateAnimation({ ...DEFAULT_ANIMATION_CONFIG, enabled: false });
  };

  const handleCopyAnimation = () => {
    animationClipboard = JSON.parse(JSON.stringify(currentConfig));
    setCopiedSuccess(true);
    setTimeout(() => setCopiedSuccess(false), 2000);
  };

  const handlePasteAnimation = () => {
    if (animationClipboard) {
      onUpdateAnimation({ ...animationClipboard });
      setPasteSuccess(true);
      setTimeout(() => setPasteSuccess(false), 2000);
    }
  };

  return (
    <div className="space-y-5 text-xs text-slate-200">
      {/* Top Controls: Preview & Copy/Paste/Reset */}
      <div className="flex items-center justify-between gap-1.5 p-2 bg-[#111B2E] rounded-xl border border-[#263750]">
        <button
          type="button"
          onClick={onPreviewAnimation}
          className="flex-1 py-1.5 px-2.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-bold flex items-center justify-center gap-1.5 shadow transition active:scale-95 text-[11px]"
          title="Animasyonu tuvalde hemen test et"
        >
          <Play className="w-3 h-3 fill-current" />
          <span>Önizle</span>
        </button>

        <button
          type="button"
          onClick={handleCopyAnimation}
          className="p-1.5 rounded-lg bg-[#1E293B] hover:bg-slate-700 text-slate-300 hover:text-white transition flex items-center justify-center"
          title="Animasyon ayarlarını kopyala"
        >
          {copiedSuccess ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
        </button>

        <button
          type="button"
          disabled={!animationClipboard}
          onClick={handlePasteAnimation}
          className="p-1.5 rounded-lg bg-[#1E293B] hover:bg-slate-700 text-slate-300 hover:text-white disabled:opacity-40 transition flex items-center justify-center text-[10px] font-bold"
          title="Kopyalanan animasyonu yapıştır"
        >
          {pasteSuccess ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : 'Yapıştır'}
        </button>

        <button
          type="button"
          onClick={handleResetAnimation}
          className="p-1.5 rounded-lg bg-[#1E293B] hover:bg-rose-950/60 text-slate-300 hover:text-rose-300 transition flex items-center justify-center"
          title="Animasyon ayarlarını sıfırla"
        >
          <RotateCcw className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Performance Advisory Warning */}
      {pageAnimationCount > 20 && (
        <div className="p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-start gap-2 text-amber-300 text-[11px] leading-relaxed">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-400" />
          <div>
            <span className="font-bold">Performans Uyarısı:</span> Bu sayfada {pageAnimationCount} animasyon etkin. Mobil cihazlarda akıcı deneyim için animasyon sayısını 15-20 ile sınırlamanız önerilir.
          </div>
        </div>
      )}

      {/* Main Switch */}
      <div className="p-3 bg-[#111B2E] rounded-xl border border-[#263750] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg bg-blue-500/20 text-blue-400 flex items-center justify-center">
            <Sparkles className="w-3.5 h-3.5" />
          </div>
          <div>
            <div className="font-bold text-slate-100">Bölüm Animasyonu</div>
            <div className="text-[10px] text-slate-400">Sayfa açılış ve kaydırma görsel efektleri</div>
          </div>
        </div>

        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={currentConfig.enabled}
            onChange={(e) => handleToggleEnabled(e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-9 h-5 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
        </label>
      </div>

      {currentConfig.enabled && (
        <div className="space-y-5 animate-in fade-in duration-200">
          {/* SECTION 1: ENTRANCE ANIMATION */}
          <div className="p-3.5 bg-[#111B2E] rounded-xl border border-[#263750] space-y-3.5">
            <div className="flex items-center justify-between text-[11px] font-bold uppercase tracking-wider text-blue-400">
              <span className="flex items-center gap-1.5">
                <Flame className="w-3.5 h-3.5" />
                Giriş Efekti (Entrance)
              </span>
              <span className="text-[10px] text-slate-400 font-mono">
                {currentConfig.entrance?.duration}ms
              </span>
            </div>

            {/* Preset Selector */}
            <div>
              <label className="block text-[10px] font-bold text-slate-300 uppercase mb-1">
                Animasyon Önayarı
              </label>
              <select
                value={currentConfig.entrance?.type || 'fade-up'}
                onChange={(e) => handleUpdateEntrance('type', e.target.value as AnimationPreset)}
                className="w-full bg-[#0B1324] border border-[#263750] rounded-lg px-2.5 py-1.5 text-xs text-slate-200 font-semibold focus:outline-none focus:border-blue-500"
              >
                {ANIMATION_PRESET_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    [{opt.category}] {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Duration Selector */}
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="text-[10px] font-bold text-slate-300 uppercase">Süre (Duration)</label>
                <span className="text-[10px] text-slate-400 font-mono">
                  {currentConfig.entrance?.duration} ms
                </span>
              </div>
              <div className="grid grid-cols-5 gap-1 mb-2">
                {[200, 300, 500, 700, 1000].map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => handleUpdateEntrance('duration', d)}
                    className={`py-1 rounded text-[10px] font-bold transition ${
                      currentConfig.entrance?.duration === d
                        ? 'bg-blue-600 text-white'
                        : 'bg-[#1E293B] text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    {d}ms
                  </button>
                ))}
              </div>
              <input
                type="range"
                min="100"
                max="2000"
                step="50"
                value={currentConfig.entrance?.duration || 700}
                onChange={(e) => handleUpdateEntrance('duration', parseInt(e.target.value, 10))}
                className="w-full h-1 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
              />
            </div>

            {/* Delay Selector */}
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="text-[10px] font-bold text-slate-300 uppercase">Gecikme (Delay)</label>
                <span className="text-[10px] text-slate-400 font-mono">
                  {currentConfig.entrance?.delay} ms
                </span>
              </div>
              <div className="grid grid-cols-4 gap-1 mb-2">
                {[0, 100, 200, 300].map((dl) => (
                  <button
                    key={dl}
                    type="button"
                    onClick={() => handleUpdateEntrance('delay', dl)}
                    className={`py-1 rounded text-[10px] font-bold transition ${
                      currentConfig.entrance?.delay === dl
                        ? 'bg-blue-600 text-white'
                        : 'bg-[#1E293B] text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    {dl}ms
                  </button>
                ))}
              </div>
              <input
                type="range"
                min="0"
                max="1000"
                step="50"
                value={currentConfig.entrance?.delay || 0}
                onChange={(e) => handleUpdateEntrance('delay', parseInt(e.target.value, 10))}
                className="w-full h-1 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
              />
            </div>

            {/* Easing Selector */}
            <div>
              <label className="block text-[10px] font-bold text-slate-300 uppercase mb-1">
                Hız Eğrisi (Easing)
              </label>
              <select
                value={currentConfig.entrance?.easing || 'ease-out'}
                onChange={(e) => handleUpdateEntrance('easing', e.target.value as AnimationEasing)}
                className="w-full bg-[#0B1324] border border-[#263750] rounded-lg px-2.5 py-1.5 text-xs text-slate-200 font-semibold focus:outline-none focus:border-blue-500"
              >
                {EASING_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Trigger Selector */}
            <div>
              <label className="block text-[10px] font-bold text-slate-300 uppercase mb-1">
                Tetikleme Türü
              </label>
              <div className="grid grid-cols-2 gap-1.5">
                <button
                  type="button"
                  onClick={() => handleUpdateEntrance('trigger', 'on-load')}
                  className={`py-1.5 px-2 rounded-lg text-[11px] font-bold transition text-center ${
                    currentConfig.entrance?.trigger === 'on-load'
                      ? 'bg-blue-600 text-white'
                      : 'bg-[#0B1324] border border-[#263750] text-slate-300 hover:bg-slate-800'
                  }`}
                >
                  Sayfa Açılışı
                </button>
                <button
                  type="button"
                  onClick={() => handleUpdateEntrance('trigger', 'on-scroll')}
                  className={`py-1.5 px-2 rounded-lg text-[11px] font-bold transition text-center ${
                    currentConfig.entrance?.trigger === 'on-scroll'
                      ? 'bg-blue-600 text-white'
                      : 'bg-[#0B1324] border border-[#263750] text-slate-300 hover:bg-slate-800'
                  }`}
                >
                  Kaydırma (Scroll)
                </button>
              </div>
            </div>
          </div>

          {/* SECTION 2: SCROLL ANIMATION (INTERSECTION OBSERVER) */}
          <div className="p-3.5 bg-[#111B2E] rounded-xl border border-[#263750] space-y-3.5">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-emerald-400">
                <ScrollText className="w-3.5 h-3.5" />
                Scroll ile Görünür Olma
              </span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={currentConfig.scroll?.enabled !== false}
                  onChange={(e) => handleUpdateScroll('enabled', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-8 h-4 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-emerald-600"></div>
              </label>
            </div>

            {currentConfig.scroll?.enabled !== false && (
              <div className="space-y-3 pt-1">
                {/* Scroll Threshold */}
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="text-[10px] font-bold text-slate-300 uppercase">
                      Tetikleme Eşiği (Threshold)
                    </label>
                    <span className="text-[10px] text-slate-400 font-mono">
                      %{Math.round((currentConfig.scroll?.threshold || 0.15) * 100)}
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-1">
                    {[0.1, 0.15, 0.2, 0.3].map((th) => (
                      <button
                        key={th}
                        type="button"
                        onClick={() => handleUpdateScroll('threshold', th)}
                        className={`py-1 rounded text-[10px] font-bold transition ${
                          currentConfig.scroll?.threshold === th
                            ? 'bg-emerald-600 text-white'
                            : 'bg-[#1E293B] text-slate-300 hover:bg-slate-700'
                        }`}
                      >
                        %{Math.round(th * 100)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Once vs Repeat */}
                <div className="flex items-center justify-between p-2 bg-[#0B1324] rounded-lg border border-[#263750]">
                  <span className="text-[11px] font-semibold text-slate-300">
                    Sadece Bir Kez Oynat (once)
                  </span>
                  <input
                    type="checkbox"
                    checked={currentConfig.scroll?.once !== false}
                    onChange={(e) => handleUpdateScroll('once', e.target.checked)}
                    className="w-4 h-4 rounded text-emerald-600 bg-slate-700 border-slate-600 focus:ring-emerald-500 cursor-pointer"
                  />
                </div>
              </div>
            )}
          </div>

          {/* SECTION 3: STAGGER SYSTEM (Grid / Cards / Child items) */}
          <div className="p-3.5 bg-[#111B2E] rounded-xl border border-[#263750] space-y-3">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-purple-400">
                <Sliders className="w-3.5 h-3.5" />
                Stagger (Kademeli Liste Geçişi)
              </span>
              <span className="text-[10px] text-slate-400 font-mono">
                {currentConfig.staggerChildren || 0}ms
              </span>
            </div>

            <p className="text-[10px] text-slate-400 leading-normal">
              Kart, hizmet veya galeri öğelerinin sırayla gecikmeli olarak belirmesini sağlar.
            </p>

            <div className="grid grid-cols-4 gap-1">
              {[0, 100, 150, 200].map((stg) => (
                <button
                  key={stg}
                  type="button"
                  onClick={() =>
                    onUpdateAnimation({
                      ...currentConfig,
                      staggerChildren: stg,
                    })
                  }
                  className={`py-1 rounded text-[10px] font-bold transition ${
                    (currentConfig.staggerChildren || 0) === stg
                      ? 'bg-purple-600 text-white'
                      : 'bg-[#1E293B] text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  {stg === 0 ? 'Kapalı' : `${stg}ms`}
                </button>
              ))}
            </div>
          </div>

          {/* SECTION 4: HOVER EFFECT */}
          <div className="p-3.5 bg-[#111B2E] rounded-xl border border-[#263750] space-y-3">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-amber-400">
                <MousePointer className="w-3.5 h-3.5" />
                Hover (Fare Üzerine Geldiğinde)
              </span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={Boolean(currentConfig.hover?.enabled)}
                  onChange={(e) => handleUpdateHover('enabled', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-8 h-4 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-amber-600"></div>
              </label>
            </div>

            {currentConfig.hover?.enabled && (
              <div className="space-y-2 pt-1">
                <div className="grid grid-cols-3 gap-1">
                  {[
                    { id: 'lift', label: 'Havalanma (Lift)' },
                    { id: 'scale', label: 'Büyüme (Scale)' },
                    { id: 'glow', label: 'Işıma (Glow)' },
                  ].map((h) => (
                    <button
                      key={h.id}
                      type="button"
                      onClick={() => handleUpdateHover('type', h.id)}
                      className={`py-1.5 px-1 rounded text-[10px] font-bold transition text-center ${
                        currentConfig.hover?.type === h.id
                          ? 'bg-amber-600 text-white'
                          : 'bg-[#1E293B] text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      {h.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* SECTION 5: RESPONSIVE TOGGLES & OVERRIDES */}
          <div className="p-3.5 bg-[#111B2E] rounded-xl border border-[#263750] space-y-3">
            <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-cyan-400">
              <Monitor className="w-3.5 h-3.5" />
              Cihaz Bazlı Animasyon Aç/Kapa
            </span>

            <div className="grid grid-cols-3 gap-1.5">
              {/* Desktop */}
              <button
                type="button"
                onClick={() =>
                  onUpdateAnimation({
                    ...currentConfig,
                    desktopEnabled: currentConfig.desktopEnabled === false ? true : false,
                  })
                }
                className={`p-2 rounded-lg flex flex-col items-center gap-1 text-[10px] font-bold transition border ${
                  currentConfig.desktopEnabled !== false
                    ? 'bg-blue-600/20 text-blue-300 border-blue-500/40'
                    : 'bg-[#0B1324] text-slate-500 border-transparent opacity-60'
                }`}
              >
                <Monitor className="w-4 h-4" />
                <span>Masaüstü: {currentConfig.desktopEnabled !== false ? 'Açık' : 'Kapalı'}</span>
              </button>

              {/* Tablet */}
              <button
                type="button"
                onClick={() =>
                  onUpdateAnimation({
                    ...currentConfig,
                    tabletEnabled: currentConfig.tabletEnabled === false ? true : false,
                  })
                }
                className={`p-2 rounded-lg flex flex-col items-center gap-1 text-[10px] font-bold transition border ${
                  currentConfig.tabletEnabled !== false
                    ? 'bg-blue-600/20 text-blue-300 border-blue-500/40'
                    : 'bg-[#0B1324] text-slate-500 border-transparent opacity-60'
                }`}
              >
                <Tablet className="w-4 h-4" />
                <span>Tablet: {currentConfig.tabletEnabled !== false ? 'Açık' : 'Kapalı'}</span>
              </button>

              {/* Mobile */}
              <button
                type="button"
                onClick={() =>
                  onUpdateAnimation({
                    ...currentConfig,
                    mobileEnabled: currentConfig.mobileEnabled === false ? true : false,
                  })
                }
                className={`p-2 rounded-lg flex flex-col items-center gap-1 text-[10px] font-bold transition border ${
                  currentConfig.mobileEnabled !== false
                    ? 'bg-blue-600/20 text-blue-300 border-blue-500/40'
                    : 'bg-[#0B1324] text-slate-500 border-transparent opacity-60'
                }`}
              >
                <Smartphone className="w-4 h-4" />
                <span>Mobil: {currentConfig.mobileEnabled !== false ? 'Açık' : 'Kapalı'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
